import { useToken } from '@common/hooks/use-token'
import { useOnboarding } from '../_hooks/use-onboarding'
import {
  Dialog,
  DialogContainer,
  DialogHeader,
  DialogSubtitle,
  DialogTopTitle,
} from './Dialog.styles'
import { Input } from '@common/components/Input'
import { ButtonContainer } from '@common/components/ButtonContainer'
import { Button } from '@common/components/Button'
import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'

export const IntegrityPolicyLink = styled(Link)`
  width: 100%;
  display: block;
  margin-top: 1rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.onBackgroundSemiLight};
  text-decoration: underline;
`

interface UseCaptureCodeProps {
  code: string
  email: string
  phoneNumber: string
  orgName: string
  defaultRemainingTime: number
  onSuccess: (token: string) => void
  onFailure: (error: string) => void
}
const useCaptureCode = ({
  code,
  email,
  phoneNumber,
  orgName,
  defaultRemainingTime,
  onSuccess,
  onFailure,
}: UseCaptureCodeProps) => {
  const { verifyCode, sendCode, loading } = useOnboarding()
  const [remainingTime, setRemainingTime] = useState(defaultRemainingTime)
  const [error, setError] = useState('')

  useEffect(() => {
    const handle = setTimeout(() => {
      setRemainingTime((prev) => {
        if (prev - 1 <= 0) {
          clearInterval(handle)

          return 0
        }

        return prev - 1
      })
    }, 1000)

    return () => clearTimeout(handle)
  }, [remainingTime])

  const handleVerifyCode = () => {
    verifyCode(code, email, phoneNumber, orgName).then(({ data }) => {
      if (data?.onboardCodeVerify.error) {
        onFailure(data.onboardCodeVerify.error)
        setError(data.onboardCodeVerify.error)
        return
      }

      if (!data?.onboardCodeVerify.accessToken) {
        onFailure('Ett fel uppstod')
        return
      }

      onSuccess(data.onboardCodeVerify.accessToken)
    })
  }

  useEffect(() => {
    if (code.length === 1) {
      setError('')
    }
    if (code.length === 4) {
      handleVerifyCode()
    }
  }, [code])

  const handleResendCode = () => {
    if (remainingTime !== 0) return

    sendCode(email, phoneNumber).then(({ data }) => {
      if (data?.onboardCodeSend?.retryInSeconds) {
        setRemainingTime(data.onboardCodeSend.retryInSeconds)
      }

      if (data?.onboardCodeSend?.error) {
        setError(data.onboardCodeSend.error)
      }
    })
  }

  return {
    loading,
    error,
    verifyCode: handleVerifyCode,
    resendCode: handleResendCode,
    remainingTime,
  }
}

export const CaptureCodeDialog = ({
  defaultRemainingTime,
  orgName,
  phoneNumber,
  email,
  onNext,
  onBack,
}: {
  defaultRemainingTime: number
  orgName: string
  phoneNumber: string
  email: string
  onNext: () => void
  onBack: () => void
}) => {
  const { setToken } = useToken()
  const [code, setCode] = useState('')
  const { loading, error, verifyCode, resendCode, remainingTime } = useCaptureCode({
    code,
    email,
    phoneNumber,
    orgName,
    defaultRemainingTime,
    onSuccess: (token) => {
      setToken(token)
      onNext()
    },
    onFailure: () => {
      setCode('')
    },
  })

  return (
    <DialogContainer
      onSubmit={(e) => {
        e.preventDefault()
        verifyCode()
      }}
    >
      <Dialog>
        <DialogTopTitle>Steg 3 av 3</DialogTopTitle>
        <DialogHeader>Ange engångskoden</DialogHeader>
        <DialogSubtitle>Engångskoden har skickats till {phoneNumber}</DialogSubtitle>
        <div style={{ marginTop: '1rem' }} />
        <Input
          value={code}
          error={error}
          placeholder="1234"
          autoFocus
          onChange={(e) => {
            if (e.currentTarget.value.length <= 4) {
              setCode(e.currentTarget.value)
            }
          }}
        />
        <div style={{ marginTop: '1rem' }} />
        <ButtonContainer>
          <Button onClick={() => resendCode()} disabled={remainingTime !== 0} loading={loading}>
            Skicka igen {remainingTime !== 0 ? `(${remainingTime})` : ''}
          </Button>
          <Button variant="secondary" onClick={() => onBack()}>
            Tillbaka
          </Button>
        </ButtonContainer>
        <IntegrityPolicyLink href="/integritetspolicy" target="_blank">
          Genom att använda Booet godkänner du vår integritetspolicy.
        </IntegrityPolicyLink>
      </Dialog>
    </DialogContainer>
  )
}
